import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-hot-toast';
import useTransactionStore from '../../store/useTransactionStore';
import { formatCurrency } from '../../utils/formatters';

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const symbols = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
const rateFormatter = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
const buttonClass = 'rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white disabled:opacity-50';

const AllocationModal = ({ isOpen, onClose, transaction, onSuccess, canSubmit = true }) => {
  const { getMatchingInvoices, allocate } = useTransactionStore();
  const [invoices, setInvoices] = useState([]);
  const [rows, setRows] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const accountId = transaction?.account?._id || transaction?.account;
  const currency = transaction?.financialAccount?.currency || transaction?.currency || 'TRY';
  const allocated = (transaction?.allocations || []).reduce((sum, item) => sum + roundMoney(item.sourceAmount ?? item.amount), 0);
  const available = Math.max(0, roundMoney(roundMoney(transaction?.amount) - allocated));
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const remaining = Math.max(0, roundMoney(available - total));
  const money = (value, code = currency) => formatCurrency(value, code);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setRows([]);
    setInvoices([]);
    setCheckedIds([]);
    setPickerOpen(false);
    setSearch('');
    setLoading(Boolean(accountId));
    if (accountId) {
      getMatchingInvoices(transaction._id).then((data) => {
        if (active) setInvoices(data);
      }).catch(() => {
        if (active) toast.error('Faturalar yüklenemedi. Lütfen tekrar deneyin.');
      }).finally(() => { if (active) setLoading(false); });
    }
    return () => { active = false; };
  }, [accountId, getMatchingInvoices, isOpen, transaction?._id]);

  const invoiceLimit = (invoice) => {
    const invoiceCurrency = invoice.currency || 'TRY';
    const sourceRate = currency === 'TRY' ? 1 : Number(transaction?.exchangeRate || 0);
    const targetRate = invoiceCurrency === 'TRY' ? 1 : Number(invoice.exchangeRate || 0);
    const rate = currency === invoiceCurrency ? 1 : sourceRate / targetRate;
    if (!Number.isFinite(rate) || rate <= 0) return { rate: 0, maximum: 0 };
    return { rate, maximum: Math.max(0, Math.floor((roundMoney(invoice.remainingAmount) / rate) * 100) / 100) };
  };
  const eligible = invoices.filter((invoice) => {
    const status = String(invoice.paymentStatus || '').toUpperCase();
    const direction = transaction?.type === 'Inflow' ? ['Sales', 'SalesReturn'] : ['Purchase', 'PurchaseReturn'];
    return ['UNPAID', 'PARTIAL', 'PARTIALLYPAID'].includes(status)
      && invoice.status !== 'Cancelled' && roundMoney(invoice.remainingAmount) > 0
      && direction.includes(invoice.type)
      && !rows.some((row) => row.invoice._id === invoice._id);
  });
  const filtered = eligible.filter((invoice) => String(invoice.invoiceNumber || '').toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')));

  const addInvoices = () => {
    let balance = remaining;
    const additions = checkedIds.map((id) => eligible.find((invoice) => invoice._id === id)).filter(Boolean).map((invoice) => {
      const amount = Math.min(balance, invoiceLimit(invoice).maximum);
      balance -= amount;
      return { invoice, amount };
    });
    setRows([...rows, ...additions]);
    setPickerOpen(false);
    setCheckedIds([]);
  };

  const changeAmount = (id, value) => {
    const row = rows.find((item) => item.invoice._id === id);
    const maximum = Math.min(invoiceLimit(row.invoice).maximum, Math.max(0, available - total + row.amount));
    const requested = roundMoney(value);
    const amount = Math.max(0, Math.min(Number.isFinite(requested) ? requested : maximum, maximum));
    if (requested > maximum || !Number.isFinite(requested)) toast.error(`En fazla ${money(maximum)} eşleştirilebilir.`, { id: 'allocation-limit' });
    setRows(rows.map((item) => item.invoice._id === id ? { ...item, amount } : item));
  };

  const submit = async () => {
    if (!canSubmit || savingRef.current || !transaction?._id) return;
    const positiveRows = rows.filter((row) => row.amount > 0);
    if (!positiveRows.length || total > available || positiveRows.some((row) => row.amount > invoiceLimit(row.invoice).maximum || roundMoney(row.amount * invoiceLimit(row.invoice).rate) <= 0)) {
      toast.error('Tutarları kontrol edin; fatura ve işlem bakiyeleri aşılamaz.');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await allocate(transaction._id, { allocations: positiveRows.map((row) => ({ invoiceId: row.invoice._id, amount: row.amount })) });
      if (!result.success) { toast.error(result.message || 'Eşleştirme kaydedilemedi.'); return; }
      toast.success('Eşleştirme kaydedildi. Kalan bakiye tekrar kullanılabilir.');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Eşleştirme kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-label="Manuel Eşleştirme" className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><h3 className="text-lg font-black">Manuel Eşleştirme</h3><p className="mt-2 text-sm text-slate-600">{transaction?.description || transaction?.category}</p><p className="mt-2 font-bold">Kullanılabilir Tutar: {money(available)}</p></div>
          <div className="flex items-center gap-2">
            <button type="button" disabled={saving || loading || !canSubmit || !accountId || available <= 0} onClick={() => { setCheckedIds([]); setSearch(''); setPickerOpen(true); }} className={`${buttonClass} flex items-center gap-2`}><Plus size={16} /> Fatura Seç / Ekle</button>
            <button type="button" aria-label="Kapat" disabled={saving} onClick={onClose}><X size={20} /></button>
          </div>
        </div>
        <div className="my-6 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50"><tr>{['Fatura No', 'Fatura Bakiyesi', `Kapatılacak Tutar (${currency})`, 'Kaldır'].map((title) => <th key={title} className="p-3">{title}</th>)}</tr></thead>
            <tbody>{rows.length === 0 ? <tr><td colSpan={4} className="p-8 text-center text-slate-400">{loading ? 'Faturalar yükleniyor...' : 'Henüz fatura seçilmedi. Fatura Seç / Ekle ile başlayın.'}</td></tr> : rows.map((row) => {
              const { rate, maximum } = invoiceLimit(row.invoice);
              return <tr key={row.invoice._id} className="border-b border-slate-100">
                <td className="p-3 font-bold">{row.invoice.invoiceNumber || 'Numarasız'}</td>
                <td className="p-3">{money(roundMoney(row.invoice.remainingAmount), row.invoice.currency || 'TRY')}</td>
                <td className="p-3">
                  <NumericFormat aria-label={`${row.invoice.invoiceNumber || 'Fatura'} kapatılacak tutar`} value={row.amount} thousandSeparator="." decimalSeparator="," allowedDecimalSeparators={[',']} decimalScale={2} fixedDecimalScale allowNegative={false} isAllowed={({ floatValue }) => {
                    const limit = Math.min(maximum, Math.max(0, available - total + row.amount));
                    if (floatValue !== undefined && (!Number.isFinite(floatValue) || roundMoney(floatValue) > limit)) {
                      changeAmount(row.invoice._id, floatValue);
                      return false;
                    }
                    return true;
                  }} inputMode="decimal" suffix={` ${symbols[currency] || currency}`} disabled={saving || !canSubmit} onFocus={(event) => { if (row.amount === 0) event.target.select(); }} onValueChange={({ floatValue }, info) => { if (info.source === 'event') changeAmount(row.invoice._id, floatValue); }} className="w-48 rounded-xl border border-slate-200 p-3 font-bold" />
                  {(row.invoice.currency || 'TRY') !== currency && (
                    <p className="mt-1 text-xs text-slate-500">
                      Fatura karşılığı: {money(roundMoney(row.amount * rate), row.invoice.currency || 'TRY')}
                      {' · Kur: '}{rate > 0
                        ? `1 ${row.invoice.currency || 'TRY'} = ${rateFormatter.format(1 / rate)} ${currency} (sabit)`
                        : 'Geçerli sabit kur bulunamadı'}
                      {' · Üst sınır: '}{money(maximum)}
                    </p>
                  )}
                </td>
                <td className="p-3"><button type="button" aria-label={`${row.invoice.invoiceNumber || 'Fatura'} kaldır`} disabled={saving} onClick={() => setRows(rows.filter((item) => item !== row))} className="p-2 text-rose-600"><Trash2 size={18} /></button></td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        <div aria-live="polite" className="space-y-3"><p className="font-bold">Eşleştirilen Toplam Tutar: {money(total)}</p><div className="rounded-2xl bg-emerald-50 p-4 text-emerald-800"><p className="font-bold">Kalan Serbest Bakiye (Avans): {money(remaining)}</p><p className="mt-1 text-sm">Bu bakiye ileride başka faturalarla eşleştirilebilir.</p></div></div>
        <button type="button" onClick={submit} disabled={!canSubmit || saving || total <= 0 || total > available} className={`${buttonClass} mt-5 flex w-full items-center justify-center gap-2`}>{saving && <Loader2 size={18} className="animate-spin" />}Eşleştirmeyi Onayla</button>
        {!canSubmit && <p className="mt-2 text-sm text-slate-500">Eşleştirme yetkiniz bulunmuyor.</p>}
      </div>
      {pickerOpen && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4">
        <div role="dialog" aria-modal="true" aria-label="Fatura seç" className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
          <div className="flex justify-between"><h4 className="font-black">Açık Faturalar</h4><button type="button" aria-label="Fatura seçimini kapat" onClick={() => setPickerOpen(false)}><X size={20} /></button></div>
          <input aria-label="Fatura ara" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Fatura numarası ara..." className="my-4 w-full rounded-xl border p-3" />
          <div className="max-h-80 space-y-2 overflow-y-auto">{filtered.length ? filtered.map((invoice) => <label key={invoice._id} className="flex items-center gap-3 rounded-xl border p-3">
            <input type="checkbox" checked={checkedIds.includes(invoice._id)} disabled={invoiceLimit(invoice).maximum <= 0} onChange={(event) => setCheckedIds(event.target.checked ? [...checkedIds, invoice._id] : checkedIds.filter((id) => id !== invoice._id))} />
            <span className="flex-1 font-bold">{invoice.invoiceNumber || 'Numarasız'}{invoiceLimit(invoice).maximum <= 0 && <small className="block text-rose-600">Geçerli kur veya eşleştirilebilir bakiye yok.</small>}</span><span>{money(roundMoney(invoice.remainingAmount), invoice.currency || 'TRY')}</span>
          </label>) : <p className="py-6 text-center text-slate-500">Eklenebilecek açık fatura bulunamadı.</p>}</div>
          <button type="button" disabled={!checkedIds.length} onClick={addInvoices} className={`${buttonClass} mt-4 w-full`}>Ekle ({checkedIds.length})</button>
        </div>
      </div>}
    </div>
  );
};

export default AllocationModal;
